import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    IconButton,
    makeStyles,
    Paper,
    Theme,
    Typography,
} from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CloseIcon from '@material-ui/icons/Close'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive'
import { ContractAlert } from '../../hooks/useContractAlerts'
import {
    ApartmentContract,
    ContractLifecycleStatus,
} from '../../models/Company/types'
import { ContractStatusChip } from './ContractStatusChip'
import { formatDate } from './utils'

const STATUS_LABELS: Record<ContractLifecycleStatus, string> = {
    active: 'Active',
    ending_soon: 'Ending soon',
    auto_renew_notice: 'Auto-renew notice',
    expired: 'Expired',
    no_dates: 'No dates',
}

const ACCENT: Record<ContractLifecycleStatus, string> = {
    active: '#22c55e',
    ending_soon: '#f59e0b',
    auto_renew_notice: '#ea580c',
    expired: '#ef4444',
    no_dates: '#6b7280',
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        borderRadius: theme.spacing(1.5),
        border: '1px solid #fde68a',
        backgroundColor: '#fffbeb',
        overflow: 'hidden',
    },
    collapsed: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.spacing(1.5),
        border: '1px solid #fde68a',
        backgroundColor: '#fffbeb',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 1.5, 1.5, 2),
        flexWrap: 'wrap',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        minWidth: 0,
    },
    icon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: '#fef3c7',
        color: '#d97706',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    summaryChips: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        padding: theme.spacing(0, 2, 1.5),
    },
    summaryChip: {
        height: 26,
        fontWeight: 600,
    },
    summaryChipActive: {
        boxShadow: '0 0 0 2px #2563eb',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 2),
        cursor: 'pointer',
        borderLeft: '4px solid transparent',
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    rowExpired: {
        backgroundColor: '#fdf2f2',
        '&:hover': {
            backgroundColor: '#f8e4e4',
        },
    },
    rowEndingSoon: {
        backgroundColor: '#fff8ea',
        '&:hover': {
            backgroundColor: '#fff0d0',
        },
    },
    rowAutoRenew: {
        backgroundColor: '#fff5ee',
        '&:hover': {
            backgroundColor: '#ffe9dc',
        },
    },
    rowNoDates: {
        backgroundColor: '#f4f5f7',
        '&:hover': {
            backgroundColor: '#eaedf1',
        },
    },
    rowActive: {
        backgroundColor: '#f2f9f3',
        '&:hover': {
            backgroundColor: '#e4f2e6',
        },
    },
    rowBody: {
        minWidth: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    rowMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    chevron: {
        color: theme.palette.text.disabled,
        flexShrink: 0,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(1, 1.5),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

interface ContractAlertsPanelProps {
    alerts: ContractAlert[]
    itemNoun?: string
    storageKey?: string
    onSelect: (contract: ApartmentContract) => void
    onOpenEmailSettings?: () => void
}

const readDismissed = (storageKey?: string): boolean => {
    if (!storageKey) return false
    try {
        return sessionStorage.getItem(storageKey) === '1'
    } catch {
        return false
    }
}

const writeDismissed = (storageKey: string | undefined, dismissed: boolean) => {
    if (!storageKey) return
    try {
        if (dismissed) {
            sessionStorage.setItem(storageKey, '1')
        } else {
            sessionStorage.removeItem(storageKey)
        }
    } catch {
        // Ignore private-mode storage errors.
    }
}

export const ContractAlertsPanel: React.FC<ContractAlertsPanelProps> = ({
    alerts,
    itemNoun = 'contract',
    storageKey,
    onSelect,
    onOpenEmailSettings,
}) => {
    const classes = useStyles()
    const [showAll, setShowAll] = useState(false)
    const [dismissed, setDismissed] = useState(() => readDismissed(storageKey))
    const [typeFilter, setTypeFilter] = useState<
        ContractLifecycleStatus | 'all'
    >('all')

    useEffect(() => {
        setDismissed(readDismissed(storageKey))
    }, [storageKey])

    const setClosed = (closed: boolean) => {
        setDismissed(closed)
        writeDismissed(storageKey, closed)
    }

    const counts = useMemo(() => {
        return alerts.reduce((acc, alert) => {
            acc[alert.alert_type] = (acc[alert.alert_type] || 0) + 1
            return acc
        }, {} as Partial<Record<ContractLifecycleStatus, number>>)
    }, [alerts])

    const visibleAlerts = useMemo(() => {
        const filtered =
            typeFilter === 'all'
                ? alerts
                : alerts.filter((alert) => alert.alert_type === typeFilter)
        return showAll ? filtered : filtered.slice(0, 5)
    }, [alerts, typeFilter, showAll])

    const filteredCount =
        typeFilter === 'all'
            ? alerts.length
            : alerts.filter((alert) => alert.alert_type === typeFilter).length

    if (alerts.length === 0) {
        if (!onOpenEmailSettings) return null
        return (
            <Box display="flex" justifyContent="flex-end">
                <Button
                    size="small"
                    startIcon={<MailOutlineIcon />}
                    onClick={onOpenEmailSettings}
                >
                    Alert email settings
                </Button>
            </Box>
        )
    }

    const title =
        itemNoun === 'certificate' ? 'Certificate alerts' : 'Contract alerts'

    if (dismissed) {
        return (
            <Paper className={classes.collapsed} elevation={0}>
                <Box className={classes.titleRow}>
                    <Box
                        className={classes.icon}
                        style={{ width: 28, height: 28 }}
                    >
                        <NotificationsActiveIcon style={{ fontSize: 16 }} />
                    </Box>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {alerts.length} {title.toLowerCase()} hidden
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    {onOpenEmailSettings && (
                        <IconButton
                            size="small"
                            aria-label="Alert email settings"
                            onClick={onOpenEmailSettings}
                        >
                            <MailOutlineIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => setClosed(false)}
                    >
                        Show
                    </Button>
                </Box>
            </Paper>
        )
    }

    const summaryOrder: ContractLifecycleStatus[] = [
        'expired',
        'ending_soon',
        'auto_renew_notice',
        'no_dates',
    ]

    return (
        <Paper className={classes.root} elevation={0}>
            <Box className={classes.header}>
                <Box className={classes.titleRow}>
                    <Box className={classes.icon}>
                        <NotificationsActiveIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 700 }}
                        >
                            {title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {alerts.length} {itemNoun}
                            {alerts.length === 1 ? '' : 's'} need attention
                        </Typography>
                    </Box>
                </Box>
                <Box className={classes.headerActions}>
                    {onOpenEmailSettings && (
                        <Button
                            size="small"
                            startIcon={<MailOutlineIcon />}
                            onClick={onOpenEmailSettings}
                        >
                            Email settings
                        </Button>
                    )}
                    <IconButton
                        size="small"
                        aria-label={`Close ${title.toLowerCase()}`}
                        onClick={() => setClosed(true)}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
            <Box className={classes.summaryChips}>
                <Chip
                    size="small"
                    label={`All ${alerts.length}`}
                    className={`${classes.summaryChip} ${
                        typeFilter === 'all' ? classes.summaryChipActive : ''
                    }`}
                    onClick={() => setTypeFilter('all')}
                />
                {summaryOrder.map((status) => {
                    const count = counts[status]
                    if (!count) return null
                    return (
                        <Chip
                            key={status}
                            size="small"
                            label={`${count} ${STATUS_LABELS[
                                status
                            ].toLowerCase()}`}
                            className={`${classes.summaryChip} ${
                                typeFilter === status
                                    ? classes.summaryChipActive
                                    : ''
                            }`}
                            style={{
                                backgroundColor:
                                    status === 'expired'
                                        ? '#fee2e2'
                                        : status === 'ending_soon'
                                        ? '#fef3c7'
                                        : status === 'auto_renew_notice'
                                        ? '#ffedd5'
                                        : '#f3f4f6',
                                color:
                                    status === 'expired'
                                        ? '#991b1b'
                                        : status === 'ending_soon'
                                        ? '#92400e'
                                        : status === 'auto_renew_notice'
                                        ? '#9a3412'
                                        : '#4b5563',
                            }}
                            onClick={() =>
                                setTypeFilter((current) =>
                                    current === status ? 'all' : status,
                                )
                            }
                        />
                    )
                })}
            </Box>
            <Box className={classes.list}>
                {visibleAlerts.map((alert) => {
                    const properties = (
                        alert.contract.apartment_names ?? []
                    ).slice(0, 2)
                    const extra =
                        (alert.contract.apartment_names ?? []).length - 2
                    return (
                        <Box
                            key={alert.contract.id}
                            className={`${classes.row} ${
                                {
                                    expired: classes.rowExpired,
                                    ending_soon: classes.rowEndingSoon,
                                    auto_renew_notice: classes.rowAutoRenew,
                                    no_dates: classes.rowNoDates,
                                    active: classes.rowActive,
                                }[alert.alert_type] || classes.rowNoDates
                            }`}
                            style={{
                                borderLeftColor:
                                    ACCENT[alert.alert_type] || '#f59e0b',
                            }}
                            onClick={() => onSelect(alert.contract)}
                        >
                            <ContractStatusChip status={alert.alert_type} />
                            <Box className={classes.rowBody}>
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 600 }}
                                    noWrap
                                >
                                    {alert.contract.name}
                                </Typography>
                                <Box className={classes.rowMeta}>
                                    {alert.contract.vendor?.name && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {alert.contract.vendor.name}
                                        </Typography>
                                    )}
                                    {properties.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {properties.join(', ')}
                                            {extra > 0 ? ` +${extra}` : ''}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {alert.contract.auto_renew &&
                                        alert.contract.end_date
                                            ? `Renews ${formatDate(
                                                  alert.contract.end_date,
                                              )}`
                                            : `Ends ${formatDate(
                                                  alert.contract.end_date,
                                              )}`}
                                    </Typography>
                                </Box>
                            </Box>
                            <ChevronRightIcon
                                fontSize="small"
                                className={classes.chevron}
                            />
                        </Box>
                    )
                })}
            </Box>
            {filteredCount > 5 && (
                <Box className={classes.footer}>
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => setShowAll((open) => !open)}
                    >
                        {showAll
                            ? 'Show fewer'
                            : `Show ${filteredCount - 5} more`}
                    </Button>
                </Box>
            )}
        </Paper>
    )
}
